import { useState, useId, type JSX } from 'react';
import { Button } from '@/components';
import { EntityDialog, FormGrid, FormField, EntityDialogFooter } from '@/components/blocks/forms';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CatalogSuggestMultiSelect } from '@/components/ui/catalog-suggest';
import type { ArrayItemFieldConfig } from '../config/schemas/types';

/**
 * Props for EmbeddedArrayItemModal component.
 */
export interface EmbeddedArrayItemModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to change open state */
  onOpenChange: (open: boolean) => void;
  /** Modal title */
  title: string;
  /** Item data for editing (null = add mode) */
  item: Record<string, unknown> | null;
  /** Field configuration for form fields */
  itemFields: readonly ArrayItemFieldConfig[];
  /** Default values for new items */
  defaultValues?: Record<string, unknown>;
  /** Callback to save the item */
  onSave: (item: Record<string, unknown>) => void;
  /** Callback to delete the item (only for edit mode) */
  onDelete?: () => void;
  /** Whether a save/delete is in progress */
  isSubmitting?: boolean;
}

/**
 * Modal for adding/editing a single embedded array item.
 *
 * Features:
 * - EntityDialog wrapper for consistent styling
 * - FormGrid with fields generated from itemFields schema
 * - Supports text, number, select, and multi-select (with CatalogSuggest)
 * - Delete button in footer (edit mode only)
 * - Save/Cancel buttons
 *
 * @example
 * ```tsx
 * <EmbeddedArrayItemModal
 *   open={modalOpen}
 *   onOpenChange={setModalOpen}
 *   title="Soglia di intervento"
 *   item={editingItem}
 *   itemFields={thresholdFields}
 *   onSave={handleSave}
 *   onDelete={editingItem ? handleDelete : undefined}
 * />
 * ```
 */
export function EmbeddedArrayItemModal({
  open,
  onOpenChange,
  title,
  item,
  itemFields,
  defaultValues,
  onSave,
  onDelete,
  isSubmitting = false,
}: EmbeddedArrayItemModalProps): JSX.Element {
  const formId = useId();
  const isEditMode = item !== null;

  // Initialize form data from item or defaults
  const [formData, setFormData] = useState<Record<string, unknown>>(() =>
    initializeFormData(item, itemFields, defaultValues)
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setFormData(initializeFormData(item, itemFields, defaultValues));
    }
    onOpenChange(nextOpen);
  };

  const handleFieldChange = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };

  // Check if form has changes (for dirty state)
  const isDirty = hasChanges(item, formData, itemFields, defaultValues);

  return (
    <EntityDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={isEditMode ? `Modifica ${title}` : `Aggiungi ${title}`}
      size="sm"
      isDirty={isDirty}
      isSaving={isSubmitting}
      formId={formId}
      footer={
        <EntityDialogFooter
          extra={
            isEditMode && onDelete ? (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={onDelete}
                disabled={isSubmitting}
              >
                Elimina
              </Button>
            ) : null
          }
          secondary={
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Annulla
            </Button>
          }
          primary={
            <Button type="submit" form={formId} disabled={isSubmitting}>
              {isSubmitting ? 'Salvataggio...' : 'Salva'}
            </Button>
          }
        />
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        <FormGrid>
          {itemFields.map((field) => (
            <FieldRenderer
              key={field.key}
              field={field}
              value={formData[field.key]}
              onChange={(value) => handleFieldChange(field.key, value)}
              disabled={isSubmitting}
            />
          ))}
        </FormGrid>
      </form>
    </EntityDialog>
  );
}

/**
 * Initialize form data from item or defaults.
 */
function initializeFormData(
  item: Record<string, unknown> | null,
  itemFields: readonly ArrayItemFieldConfig[],
  defaultValues?: Record<string, unknown>
): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const field of itemFields) {
    if (item !== null && field.key in item) {
      data[field.key] = item[field.key];
    } else if (defaultValues && field.key in defaultValues) {
      data[field.key] = defaultValues[field.key];
    } else {
      // Set default based on field type
      data[field.key] = field.type === 'multi-select' ? [] : '';
    }
  }
  return data;
}

/**
 * Check if form data has changed from original item or defaults.
 */
function hasChanges(
  item: Record<string, unknown> | null,
  formData: Record<string, unknown>,
  itemFields: readonly ArrayItemFieldConfig[],
  defaultValues?: Record<string, unknown>
): boolean {
  const original = initializeFormData(item, itemFields, defaultValues);
  for (const field of itemFields) {
    const originalValue = original[field.key];
    const currentValue = formData[field.key];
    if (field.type === 'multi-select') {
      const origArray = Array.isArray(originalValue) ? originalValue : [];
      const currArray = Array.isArray(currentValue) ? currentValue : [];
      if (origArray.length !== currArray.length) return true;
      for (let i = 0; i < origArray.length; i++) {
        if (origArray[i] !== currArray[i]) return true;
      }
    } else if (originalValue !== currentValue) {
      return true;
    }
  }
  return false;
}

/**
 * Renders a single form field based on its configuration.
 */
function FieldRenderer({
  field,
  value,
  onChange,
  disabled,
}: {
  field: ArrayItemFieldConfig;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
}): JSX.Element {
  const fieldId = useId();

  switch (field.type) {
    case 'text':
      return (
        <FormField label={field.label} htmlFor={fieldId}>
          <Input
            id={fieldId}
            type="text"
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
          />
        </FormField>
      );

    case 'number':
      return (
        <FormField label={field.label} htmlFor={fieldId}>
          <Input
            id={fieldId}
            type="number"
            value={value === '' || value === null || value === undefined ? '' : String(value)}
            onChange={(e) => {
              const num = e.target.value === '' ? '' : Number(e.target.value);
              onChange(num);
            }}
            placeholder={field.placeholder}
            disabled={disabled}
          />
        </FormField>
      );

    case 'select':
      return (
        <FormField label={field.label} htmlFor={fieldId}>
          <Select value={String(value ?? '')} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger id={fieldId}>
              <SelectValue placeholder={field.placeholder ?? 'Seleziona...'} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      );

    case 'multi-select':
      return (
        <FormField label={field.label} htmlFor={fieldId}>
          {field.catalogId ? (
            <CatalogSuggestMultiSelect
              catalogId={field.catalogId}
              value={Array.isArray(value) ? (value as string[]) : []}
              onValueChange={(newValue) => onChange(newValue)}
              placeholder={field.placeholder ?? 'Aggiungi...'}
              disabled={disabled}
            />
          ) : (
            // Fallback for multi-select without catalog (not typically used)
            <div className="text-sm text-text-secondary">
              Multi-select senza catalogo non supportato
            </div>
          )}
        </FormField>
      );

    default:
      return (
        <FormField label={field.label}>
          <div className="text-sm text-text-secondary">Tipo campo non supportato: {field.type}</div>
        </FormField>
      );
  }
}
